import { useState } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';

interface CopyableFriendCodeProps {
  code: string;
}

export const CopyableFriendCode = ({ code }: CopyableFriendCodeProps) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const copyToClipboard = async () => {
    try {
      const cleanCode = code.replace(/-/g, '');
      
      // Utilisation de try/catch pour gérer les erreurs potentielles
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(cleanCode);
        setCopyState('copied');
      } else {
        // Fallback pour les navigateurs plus anciens
        const textArea = document.createElement('textarea');
        textArea.value = cleanCode;
        
        // Rendre l'élément invisible
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopyState('copied');
        } else {
          setCopyState('error');
        }
      }
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      setCopyState('error');
    }
    
    // Réinitialiser l'état après 2 secondes
    setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <div className="flex items-center gap-2 w-full">
      <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg font-mono text-sm border border-gray-200 text-center">
        {code}
      </code>
      <button
        onClick={copyToClipboard}
        className={`shrink-0 p-2 rounded-lg transition-colors ${
          copyState === 'copied'
            ? 'bg-green-100 text-green-600'
            : copyState === 'error'
            ? 'bg-red-100 text-red-600'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
        }`}
        title={
          copyState === 'copied' 
            ? 'Copié !' 
            : copyState === 'error'
            ? 'Erreur de copie, essayez à nouveau'
            : 'Copier le code'
        }
        aria-label={
          copyState === 'copied' 
            ? 'Code copié' 
            : copyState === 'error'
            ? 'Erreur de copie'
            : 'Copier le code'
        }
      >
        {copyState === 'copied' ? (
          <Check className="w-5 h-5" />
        ) : copyState === 'error' ? (
          <AlertCircle className="w-5 h-5" />
        ) : (
          <Copy className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}; 